import React, { useState } from 'react';
import Modal from './Modal';
import { contactsApi } from '../api/client';

interface AddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContactAdded: () => void;
}

const AddContactModal: React.FC<AddContactModalProps> = ({ isOpen, onClose, onContactAdded }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    category: 'friend' as 'family' | 'friend' | 'professional' | 'emergency' | 'business',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      displayName: '',
      category: 'friend',
      email: '',
      phone: '',
      company: '',
      jobTitle: '',
      notes: ''
    });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const contactData = {
        firstName: formData.firstName,
        lastName: formData.lastName || undefined,
        displayName: formData.displayName || `${formData.firstName} ${formData.lastName}`.trim(),
        category: formData.category,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        company: formData.company || undefined,
        jobTitle: formData.jobTitle || undefined,
        notes: formData.notes || undefined
      };

      await contactsApi.createContact(contactData);

      setSuccess(true);
      setTimeout(() => {
        onContactAdded();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding contact:', err);
      setError('Failed to add contact. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Contact">
      <form onSubmit={handleSubmit} className="modal-form">
        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {success && (
          <div className="modal-success">
            Contact added successfully!
          </div>
        )}

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>First Name *</label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="First name"
              disabled={loading}
            />
          </div>

          <div className="modal-form-group">
            <label>Last Name</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              placeholder="Last name"
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-group">
          <label>Display Name</label>
          <input
            type="text"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            placeholder="How you want to display this contact"
            disabled={loading}
          />
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
              disabled={loading}
            >
              <option value="friend">Friend</option>
              <option value="family">Family</option>
              <option value="professional">Professional</option>
              <option value="business">Business</option>
              <option value="emergency">Emergency</option>
            </select>
          </div>

          <div className="modal-form-group">
            <label>Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1-555-123-4567"
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-group">
          <label>Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="contact@example.com"
            disabled={loading}
          />
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Company</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Company name"
              disabled={loading}
            />
          </div>

          <div className="modal-form-group">
            <label>Job Title</label>
            <input
              type="text"
              value={formData.jobTitle}
              onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
              placeholder="Job title"
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-group">
          <label>Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes about this contact..."
            disabled={loading}
            rows={2}
          />
        </div>

        <div className="modal-form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-btn modal-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="modal-btn modal-btn-primary"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Contact'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddContactModal;